import { useEffect, useMemo, useState } from 'react';
import { Award, BookOpen, ChevronLeft, TrendingUp } from 'lucide-react';
import { useStore } from '../store/useStore';
import { ZONES } from '../types';
import { distributionOf, zone2Delta } from '../engine/weekly';
import { carriedOf, completedOf, tagsOf, type WeekSnapshot } from '../engine/notebook';
import { formatFullDate } from '../engine/dates';
import { ShareButton } from '../components/ShareButton';
import { WeekReviewCard } from '../components/insights/WeekReviewCard';
import { WhereWeeksGo } from '../components/insights/WhereWeeksGo';
import { weeksNewestFirst } from '../engine/insights';
import { weeklyInsightsEnabled } from '../native/insightsGate';

const ZONE_BY_ID = Object.fromEntries(ZONES.map((z) => [z.id, z]));

/** Mastery Log — weekly performance analytics and history. */
export function MasteryScreen() {
  const weekHistory = useStore((s) => s.weekHistory);
  const archive = useStore((s) => s.archive);
  const boundWeeks = useStore((s) => s.boundWeeks);
  const hydrateNotebook = useStore((s) => s.hydrateNotebook);
  /** The Notebook: which bound week is open for reading (null = shelf). */
  const [openWeekId, setOpenWeekId] = useState<string | null>(null);

  // Notebook hydration: pull cloud-archived weeks this device hasn't seen
  // (and retry-push any local weeks the cloud is missing). Fire-and-forget;
  // signed-out/unconfigured resolves to [] and the local shelf stands alone.
  useEffect(() => {
    let cancelled = false;
    void import('../sync/firebaseSync').then(async (m) => {
      const cloud = await m.syncNotebook(useStore.getState().boundWeeks);
      if (!cancelled && cloud.length > 0) hydrateNotebook(cloud);
    });
    return () => {
      cancelled = true;
    };
  }, [hydrateNotebook]);

  // Current-week live distribution, falling back to the latest closed week.
  const dist = useMemo(() => {
    if (archive.length > 0) return distributionOf(archive);
    return (
      weekHistory[0]?.distribution ?? { zone1: 0, zone2: 0, zone3: 0, zone4: 0 }
    );
  }, [archive, weekHistory]);

  // vs the week before the one shown (weekly.zone2Delta — 18 Oct 2026 fix).
  const delta = zone2Delta(dist, archive.length, weekHistory) ?? 0;

  const lastBound = useMemo(() => weeksNewestFirst(boundWeeks)[0], [boundWeeks]);

  const openWeek = openWeekId ? boundWeeks[openWeekId] : undefined;
  if (openWeek) {
    return <BoundWeekView snap={openWeek} onBack={() => setOpenWeekId(null)} />;
  }

  return (
    <div className="mx-auto w-full max-w-md space-y-5 px-4 py-5 lg:max-w-5xl lg:px-8 lg:py-8">
      {/* Mastery header */}
      <div className="flex items-center gap-4 rounded-2xl border border-emerald-200 dark:border-emerald-500/25 bg-gradient-to-r from-emerald-50 dark:from-emerald-950/20 to-brand-50 dark:to-brand-900/10 p-4 shadow-xl">
        <span className="rounded-full border border-emerald-300 dark:border-emerald-400/20 bg-emerald-500/10 p-3 text-emerald-600 dark:text-emerald-400 shadow-inner">
          <Award size={30} />
        </span>
        <div className="space-y-1">
          <span className="rounded border border-emerald-200 dark:border-emerald-500/10 bg-emerald-500/5 px-2 py-0.5 text-[11px] font-extrabold uppercase tracking-widest text-emerald-600 dark:text-emerald-400">
            Mastery Status
          </span>
          <h3 className="text-sm font-black text-slate-900 dark:text-white">Zone 2 Focus: {dist.zone2}%</h3>
          {archive.length > 0 && (
            <p className="text-[11px] text-slate-600 dark:text-slate-300">
              You closed {archive.length} loop{archive.length === 1 ? '' : 's'} this week —
              that's real progress.
            </p>
          )}
          {delta !== 0 && (
            <p className="flex items-center gap-0.5 text-[11px] font-bold text-emerald-600 dark:text-emerald-400">
              <TrendingUp size={12} />
              {delta > 0 ? '+' : ''}
              {delta}% vs previous week
            </p>
          )}
        </div>
      </div>

      {/* Weekly Insights (web only): last week's review, re-readable. */}
      {weeklyInsightsEnabled() && lastBound && <WeekReviewCard snap={lastBound} title="Last week" />}

      <div className="flex items-end justify-between gap-3">
        <div className="space-y-1">
          <h2 className="text-base font-black text-slate-900 dark:text-white">Focus allocation</h2>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Where your completed work landed{archive.length > 0 ? ' this week' : ' last week'}.
          </p>
        </div>
        <ShareButton compact />
      </div>

      {/* Distribution bars */}
      <div className="space-y-3 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4">
        {ZONES.map((zone) => (
          <div key={zone.id} className="space-y-1">
            <div className="flex justify-between text-[11px] font-bold">
              <span className={zone.color.text}>
                {zone.emoji} {zone.title}
              </span>
              <span className="text-slate-600 dark:text-slate-300">{dist[zone.id]}%</span>
            </div>
            <div className="h-2 w-full overflow-hidden rounded-full bg-slate-50 dark:bg-slate-950">
              <div
                className={`h-full transition-all duration-500 ${zone.color.bar}`}
                style={{ width: `${dist[zone.id]}%` }}
              />
            </div>
          </div>
        ))}
      </div>

      {/* Weekly Insights (web only): where your weeks go. */}
      <WhereWeeksGo />

      {/* Weekly archives — The Notebook shelf */}
      <div className="space-y-3">
        <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
          Weekly Performance Archives
        </h4>
        {weekHistory.length === 0 && (
          <p className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 text-center text-xs italic text-slate-500">
            No closed weeks yet — your first "Start New Week" wrap-up lands here.
          </p>
        )}
        {weekHistory.map((week) => {
          const bound = boundWeeks[week.id];
          const inner = (
            <>
              <div>
                <h5 className="flex items-center gap-1.5 text-xs font-bold text-slate-900 dark:text-white">
                  {week.label}
                  {bound ? (
                    <span className="inline-flex items-center gap-1 rounded border border-brand-200 dark:border-brand-500/20 bg-brand-500/5 px-1.5 py-0.5 text-[10px] font-extrabold uppercase tracking-wide text-brand-600 dark:text-brand-400">
                      <BookOpen size={10} /> Open
                    </span>
                  ) : (
                    /* A row with no page. Say so, rather than leaving a
                       blank space that reads as a broken button. Two
                       causes, one honest sentence (see the footnote):
                       the week closed before archives existed, or it was
                       closed on an app version that couldn't write one. */
                    <span className="inline-flex items-center rounded border border-slate-200 dark:border-slate-700 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-slate-400 dark:text-slate-500">
                      Summary only
                    </span>
                  )}
                </h5>
                <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">
                  Zone 2 mastery: {week.distribution.zone2}%
                </p>
              </div>
              <div className="text-right">
                <span className="block text-xs font-black text-brand-600 dark:text-brand-400">
                  {week.completedCount} completed
                </span>
                <span className="block text-[11px] text-slate-500">
                  {week.rolledOverCount} rolled over
                </span>
              </div>
            </>
          );
          const rowClass =
            'flex w-full items-center justify-between rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 text-left';
          return bound ? (
            <button
              key={week.id}
              type="button"
              onClick={() => setOpenWeekId(week.id)}
              className={`${rowClass} transition-colors hover:border-brand-300 dark:hover:border-brand-500/40`}
            >
              {inner}
            </button>
          ) : (
            <div key={week.id} className={rowClass}>
              {inner}
            </div>
          );
        })}
        {weekHistory.length > 0 && Object.keys(boundWeeks).length === 0 && (
          <p className="px-1 text-[11px] italic text-slate-500 dark:text-slate-400">
            Full-page archives begin with your next Start New Week — earlier
            weeks kept their stats only.
          </p>
        )}
        {weekHistory.some((w) => !boundWeeks[w.id]) &&
          Object.keys(boundWeeks).length > 0 && (
            <p className="px-1 text-[11px] italic text-slate-500 dark:text-slate-400">
              &ldquo;Summary only&rdquo; weeks kept their figures but not their
              full page — they were closed before archives began, or on an
              older version of the app. Every week you close from now on is
              archived in full, and opens on any device you sign in to.
            </p>
          )}
      </div>
    </div>
  );
}

/** Read-only viewer for one bound week — the opened notebook page. All
 *  tag names/colors resolve from the snapshot's OWN frozen legend, so
 *  tags deleted from the live rail still render here (founder rule). */
function BoundWeekView({ snap, onBack }: { snap: WeekSnapshot; onBack: () => void }) {
  const completed = completedOf(snap);
  const carried = carriedOf(snap);
  return (
    <div className="mx-auto w-full max-w-md space-y-5 px-4 py-5 lg:max-w-5xl lg:px-8 lg:py-8">
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-1 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-2.5 py-1.5 text-xs font-bold text-slate-600 dark:text-slate-300 transition-colors hover:border-brand-300"
        >
          <ChevronLeft size={14} /> Archives
        </button>
        <div>
          <h2 className="flex items-center gap-1.5 text-sm font-black text-slate-900 dark:text-white">
            <BookOpen size={15} className="text-brand-500" /> {snap.label}
          </h2>
          <p className="text-[11px] text-slate-500 dark:text-slate-400">
            Bound {formatFullDate(new Date(snap.endedAt))} · read-only — exactly as the
            week closed
          </p>
        </div>
      </div>

      {/* Weekly Insights (web only): this week's review, on its own page. */}
      {weeklyInsightsEnabled() && <WeekReviewCard snap={snap} title="Your week" />}

      <BoundSection title={`Completed (${completed.length})`} tasks={completed} snap={snap} />
      <BoundSection title={`Carried forward (${carried.length})`} tasks={carried} snap={snap} />
    </div>
  );
}

function BoundSection({
  title,
  tasks,
  snap,
}: {
  title: string;
  tasks: WeekSnapshot['tasks'];
  snap: WeekSnapshot;
}) {
  return (
    <div className="space-y-2">
      <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
        {title}
      </h4>
      {tasks.length === 0 && (
        <p className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-3 text-center text-[11px] italic text-slate-500">
          Nothing here this week.
        </p>
      )}
      {tasks.map((task) => {
        const zone = ZONE_BY_ID[task.zone];
        const tags = tagsOf(snap, task);
        return (
          <div
            key={task.id}
            className="space-y-1.5 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-3"
          >
            <div className="flex items-start justify-between gap-2">
              <p
                className={`text-xs font-bold ${
                  task.done
                    ? 'text-slate-500 dark:text-slate-400 line-through decoration-slate-400/60'
                    : 'text-slate-900 dark:text-white'
                }`}
              >
                {task.text}
              </p>
              {zone && (
                <span className={`shrink-0 text-[11px] font-bold ${zone.color.text}`}>
                  {zone.emoji}
                </span>
              )}
            </div>
            {(tags.length > 0 || task.deadlineHint || task.deadlineAt) && (
              <div className="flex flex-wrap items-center gap-1.5">
                {tags.map((tag) => (
                  <span
                    key={tag.id}
                    className="inline-flex items-center gap-1 rounded-full border border-slate-200 dark:border-slate-700 px-1.5 py-0.5 text-[10px] font-semibold text-slate-600 dark:text-slate-300"
                  >
                    <span
                      className="h-2 w-2 rounded-full"
                      style={{ backgroundColor: tag.color }}
                    />
                    {tag.name}
                  </span>
                ))}
                {(task.deadlineHint || task.deadlineAt) && (
                  <span className="rounded-full border border-emerald-300/60 dark:border-emerald-500/30 bg-emerald-500/5 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700 dark:text-emerald-400">
                    {task.deadlineHint ?? formatFullDate(new Date(task.deadlineAt!))}
                  </span>
                )}
              </div>
            )}
            {task.steps.length > 0 && (
              <ul className="space-y-0.5 pl-0.5">
                {task.steps.map((step) => (
                  <li
                    key={step.id}
                    className={`text-[11px] ${
                      step.done
                        ? 'text-slate-400 dark:text-slate-500 line-through'
                        : 'text-slate-600 dark:text-slate-300'
                    }`}
                  >
                    {step.done ? '✓' : '○'} {step.text}
                  </li>
                ))}
              </ul>
            )}
            {task.note && (
              <p className="rounded-lg bg-slate-50 dark:bg-slate-950 px-2 py-1.5 text-[11px] italic text-slate-500 dark:text-slate-400">
                {task.note}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}
